import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import firebase from "../auth/firebase";
import { getStorage } from "../auth/storage";
import { storeFirebaseToken } from "../auth/firebaseSession";
import { USER_ROLES } from "../utility/Enum";
import { LOGIN_ROUTE } from "../routes";
import { clearPendingEmailChange } from "../auth/pendingEmailChange";
import { clearPendingMobileChange } from "../auth/mobileChange";
import { clearPendingEmailSignIn } from "../auth/pendingEmailSignIn";
import { describeConsoleSession, hasFirebaseAction, hasManagerRole, loadConsoleSession, readCachedSessionAccess, requestCoreData } from "../auth/consoleSession";

const AuthContext = createContext();
const AUTH_STORAGE_KEYS = [
  "token", "user", "refreshToken", "tokenLastValidity", "firebaseUid",
  "impersonate", "activeClubId", "preImpersonationActiveClubId", "lastLoginMethod",
];
const TRANSIENT_TOKEN_ERRORS = new Set([
  "auth/internal-error", "auth/network-request-failed", "auth/too-many-requests",
]);

const clearCachedAuthentication = () => {
  [localStorage, sessionStorage].forEach((storage) => {
    AUTH_STORAGE_KEYS.forEach((key) => storage.removeItem(key));
  });
};

const readCachedUser = (key) => {
  try { return JSON.parse(getStorage().getItem(key)) || {}; }
  catch (_) { return {}; }
};

const cachedState = () => ({
  token: getStorage().getItem("token"),
  user: readCachedUser("user"),
  impersonate: readCachedUser("impersonate"),
});

const canReuseCachedAuthentication = (user, error) => {
  const storage = getStorage();
  const lastValidity = Number(storage.getItem("tokenLastValidity"));
  return Boolean(
    TRANSIENT_TOKEN_ERRORS.has(error?.code) && storage.getItem("token") &&
    storage.getItem("user") && storage.getItem("firebaseUid") === user.uid &&
    Number.isFinite(lastValidity) && lastValidity + 30 * 60 * 1000 > Date.now()
  );
};

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState(cachedState);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [sessionStatus, setSessionStatus] = useState("loading");
  const [session, setSession] = useState(null);
  const identityRef = useRef(null);
  const requestRef = useRef(null);
  const resolvedRef = useRef(null);
  const generationRef = useRef(0);
  const logoutRef = useRef(false);
  const mountedRef = useRef(true);
  const inlineSignInRef = useRef(null);
  const inlineFailureRef = useRef(null);
  const [inlineSignInPending, setInlineSignInPending] = useState(false);
  const isInlineSignInPending = useCallback(() => Boolean(inlineSignInRef.current), []);

  const saveSession = useCallback((result, token) => {
    const storage = getStorage();
    const impersonating = Boolean(storage.getItem("impersonate"));
    storage.setItem(impersonating ? "impersonate" : "user", JSON.stringify(result.user));
    if (result.user.club?._id) storage.setItem("activeClubId", String(result.user.club._id));
    else storage.removeItem("activeClubId");
    setAuthState({
      token,
      user: impersonating ? readCachedUser("user") : result.user,
      impersonate: impersonating ? result.user : {},
    });
    setSession(result);
    setSessionStatus("ready");
  }, []);

  const refreshSession = useCallback((options = {}) => {
    const identity = options.firebaseUser || identityRef.current || firebase.auth().currentUser;
    if (!identity || logoutRef.current) return Promise.reject(new Error("session_identity_changed"));
    inlineFailureRef.current = null;
    identityRef.current = identity;
    setFirebaseUser(identity);
    const key = identity.uid + ":" + (getStorage().getItem("impersonate") || "");
    if (!options.force && requestRef.current?.key === key) return requestRef.current.promise;
    if (!options.force && resolvedRef.current?.key === key) return Promise.resolve(resolvedRef.current.result);

    const generation = ++generationRef.current;
    const isCurrent = () => mountedRef.current && !logoutRef.current &&
      generation === generationRef.current && identityRef.current?.uid === identity.uid &&
      (!options.isCurrent || options.isCurrent());
    if (!options.keepCurrentSession) setSessionStatus("loading");
    const promise = (async () => {
      try {
        const token = options.token || await identity.getIdToken(options.forceTokenRefresh !== false);
        if (!isCurrent()) throw new Error("session_identity_changed");
        const storage = getStorage();
        const previousUid = storage.getItem("firebaseUid");
        if ((previousUid && previousUid !== identity.uid) ||
          (!previousUid && (storage.getItem("user") || storage.getItem("impersonate")))) {
          clearCachedAuthentication();
        }
        // Reusing Firebase's cached token must not extend the existing refresh
        // interval or the cached-authentication fallback window.
        const reusedToken = options.forceTokenRefresh === false &&
          previousUid === identity.uid && storage.getItem("token") === token;
        if (!reusedToken) storeFirebaseToken(identity, token);
        setAuthState(cachedState());
        const request = (query, variables) => {
          if (!isCurrent()) return Promise.reject(new Error("session_identity_changed"));
          return requestCoreData(query, variables, {
            withoutClub: true, sessionToken: token, expectedFirebaseUid: identity.uid,
          });
        };
        const result = await loadConsoleSession({
          firebaseUser: identity, storage: getStorage(), request,
          registerIdentity: options.registerIdentity !== false,
        });
        if (!isCurrent()) throw new Error("session_identity_changed");
        saveSession(result, token);
        resolvedRef.current = { key, result };
        return result;
      } catch (error) {
        if (isCurrent()) {
          if (canReuseCachedAuthentication(identity, error)) {
            const cached = cachedState();
            const user = getStorage().getItem("impersonate") ? cached.impersonate : cached.user;
            const access = readCachedSessionAccess(user);
            if (!access) {
              // Older/incomplete caches cannot prove whether club setup is
              // appropriate for this account. Let the user retry resolution.
              setSessionStatus("error");
              throw new Error("registration_session_error");
            }
            const result = describeConsoleSession({ user, club: user.club, clubs: user.clubs },
              getStorage().getItem("activeClubId"), Boolean(getStorage().getItem("impersonate")), access);
            saveSession(result, cached.token);
            return result;
          }
          if (error?.code?.startsWith('auth/')) {
            clearCachedAuthentication();
            setAuthState({});
          }
          if (!options.keepCurrentSession) setSessionStatus("error");
        }
        throw error;
      } finally {
        if (requestRef.current?.generation === generation) requestRef.current = null;
      }
    })();
    requestRef.current = { key, promise, generation };
    return promise;
  }, [saveSession]);

  const completeEmailCodeSignIn = useCallback(async ({ email, signInLink, isCurrent = () => true }) => {
    const nativeAuth = firebase.auth();
    if (inlineSignInRef.current || logoutRef.current || !isCurrent()) throw new Error('session_identity_changed');
    if (!nativeAuth.isSignInWithEmailLink(signInLink)) throw new Error('email_code_unavailable');
    const operation = { initialUid: nativeAuth.currentUser?.uid || null, email, cancelled: false, expectedUid: null, replacement: null };
    inlineSignInRef.current = operation;
    setInlineSignInPending(true);
    ++generationRef.current;
    requestRef.current = null;
    resolvedRef.current = null;
    const assertCurrent = () => {
      if (!mountedRef.current || logoutRef.current || operation.cancelled || inlineSignInRef.current !== operation || !isCurrent()) {
        throw new Error('session_identity_changed');
      }
    };
    let authenticated = false;
    let completedUser;
    try {
      // The observer waits for this owner even though no action URL is visited.
      await nativeAuth.setPersistence(firebase.auth.Auth?.Persistence?.LOCAL || 'local');
      assertCurrent();
      if ((nativeAuth.currentUser?.uid || null) !== operation.initialUid) throw new Error('session_identity_changed');
      localStorage.setItem('rememberMe', '1');
      const result = await nativeAuth.signInWithEmailLink(email, signInLink);
      completedUser = result.user;
      assertCurrent();
      const user = result.user;
      if (!user?.uid || nativeAuth.currentUser?.uid !== user.uid ||
        !user.emailVerified || user.email?.trim().toLowerCase() !== email) throw new Error('session_identity_changed');
      authenticated = true;
      operation.expectedUid = user.uid;
      clearPendingEmailSignIn();
      const token = await user.getIdToken(true);
      assertCurrent();
      if (nativeAuth.currentUser?.uid !== user.uid) throw new Error('session_identity_changed');
      storeFirebaseToken(user, token);
      localStorage.setItem('lastLoginMethod', 'email');
      identityRef.current = user;
      setFirebaseUser(user);
      // Reauthentication for a pending settings link belongs to its existing
      // handler. Release the inline owner before that handler resumes.
      if (hasFirebaseAction()) {
        setSessionStatus('callback');
        return { user, callback: true };
      }
      return await refreshSession({ firebaseUser: user, token, force: true,
        isCurrent: () => !operation.cancelled && isCurrent() && nativeAuth.currentUser?.uid === user.uid });
    } catch (error) {
      if ((!isCurrent() || operation.cancelled || !mountedRef.current) && completedUser?.uid &&
        nativeAuth.currentUser?.uid === completedUser.uid) {
        await nativeAuth.signOut().catch(() => {});
      }
      if (inlineSignInRef.current === operation && mountedRef.current && !operation.cancelled && isCurrent()) {
        if (authenticated && nativeAuth.currentUser?.uid === completedUser?.uid) {
          inlineFailureRef.current = completedUser.uid;
          setSessionStatus('error');
        }
        else if (!nativeAuth.currentUser) setSessionStatus('signed-out');
      }
      throw error;
    } finally {
      if (inlineSignInRef.current === operation) {
        inlineSignInRef.current = null;
        if (mountedRef.current) setInlineSignInPending(false);
        if (mountedRef.current && !logoutRef.current && operation.replacement && operation.replacement.uid === nativeAuth.currentUser?.uid) {
          if (hasFirebaseAction()) setSessionStatus('callback');
          else refreshSession({ firebaseUser: operation.replacement }).catch(() => {});
        }
      }
    }
  }, [refreshSession]);

  const completeEmailCodeChange = useCallback(async ({ verify, email, uid, isCurrent = () => true,
    recovery = {}, onVerified = () => {} }) => {
    const nativeAuth = firebase.auth();
    const initialUser = nativeAuth.currentUser;
    if (!uid || nativeAuth.currentUser?.uid !== uid || inlineSignInRef.current || logoutRef.current ||
        getStorage().getItem('impersonate') || !isCurrent()) throw new Error('session_identity_changed');
    const operation = { kind: 'emailChange', expectedUid: uid, email, cancelled: false, replacement: null };
    inlineSignInRef.current = operation;
    setInlineSignInPending(true);
    ++generationRef.current;
    requestRef.current = null;
    resolvedRef.current = null;
    const assertCurrent = () => {
      if (!mountedRef.current || logoutRef.current || operation.cancelled || !isCurrent() ||
          nativeAuth.currentUser?.uid !== uid || getStorage().getItem('impersonate')) throw new Error('session_identity_changed');
    };
    let completedUser;
    let verificationCommitted = false;
    let sessionRecoveryRequired = false;
    try {
      let result = recovery.result;
      if (!result) {
        try { result = await verify(); }
        catch (error) {
          if (!error.verificationOutcomeUnknown) throw error;
          sessionRecoveryRequired = true;
          assertCurrent();
          // The bounded challenge retry could not recover its response. Read the
          // original Firebase session before allowing Manager to keep using it.
          await initialUser.reload();
          assertCurrent();
          if (nativeAuth.currentUser !== initialUser || !initialUser.emailVerified ||
              initialUser.email?.trim().toLowerCase() !== email) throw new Error('email_change_auth_required');
          completedUser = initialUser;
          result = { user: { _id: authState.user?._id, email } };
        }
      }
      if (result.user?._id !== authState.user?._id || result.user?.email?.trim().toLowerCase() !== email) {
        throw new Error('session_identity_changed');
      }
      if (recovery.uid && recovery.uid !== uid) throw new Error('session_identity_changed');
      verificationCommitted = true;
      assertCurrent();
      // The code has already committed the email change. Retain progress only
      // in this caller's memory so token/Core failures do not redeem it again.
      recovery.result = result;
      recovery.uid = uid;
      onVerified();
      completedUser = completedUser || recovery.user;
      if (!completedUser) {
        const credential = await nativeAuth.signInWithCustomToken(result.customToken);
        completedUser = credential.user;
      }
      assertCurrent();
      if (completedUser?.uid !== uid || !completedUser.emailVerified ||
          completedUser.email?.trim().toLowerCase() !== email ||
          nativeAuth.currentUser?.email?.trim().toLowerCase() !== email) throw new Error('session_identity_changed');
      recovery.user = completedUser;
      recovery.result = { user: result.user }; // The exchanged credential is no longer needed.
      const token = await completedUser.getIdToken(true);
      assertCurrent();
      const sessionResult = await refreshSession({ firebaseUser: completedUser, token, force: true,
        keepCurrentSession: true, registerIdentity: false,
        isCurrent: () => !operation.cancelled && isCurrent() && nativeAuth.currentUser?.uid === uid });
      if (sessionRecoveryRequired && (sessionResult.user?._id !== result.user._id ||
          sessionResult.user?.email?.trim().toLowerCase() !== email)) throw new Error('email_change_auth_required');
      delete recovery.result; delete recovery.user; delete recovery.uid;
      return sessionResult;
    } catch (error) {
      const invalidateSession = sessionRecoveryRequired || operation.cancelled || !isCurrent() || !mountedRef.current;
      if (invalidateSession) {
        delete recovery.result; delete recovery.user; delete recovery.uid;
      }
      if (invalidateSession &&
          nativeAuth.currentUser?.uid === uid &&
          nativeAuth.currentUser === (completedUser || (verificationCommitted || sessionRecoveryRequired ? initialUser : null))) {
        // A successful verification also invalidates the original session when
        // its dialog was disposed before the custom-token exchange started.
        operation.cancelled = true;
        ++generationRef.current;
        requestRef.current = null;
        resolvedRef.current = null;
        identityRef.current = null;
        inlineFailureRef.current = uid;
        clearCachedAuthentication();
        if (mountedRef.current) {
          setAuthState({}); setFirebaseUser(null); setSession(null); setSessionStatus('signed-out');
        }
        await nativeAuth.signOut().catch(() => {});
      }
      throw error;
    } finally {
      if (inlineSignInRef.current === operation) {
        inlineSignInRef.current = null;
        if (mountedRef.current) setInlineSignInPending(false);
        if (mountedRef.current && !logoutRef.current && operation.replacement?.uid === nativeAuth.currentUser?.uid) {
          refreshSession({ firebaseUser: operation.replacement }).catch(() => {});
        }
      }
    }
  }, [authState.user?._id, refreshSession]);

  useEffect(() => {
    mountedRef.current = true;
    let initialNotification = true;
    // A verified Firebase session can exist before Core registration succeeds.
    const hadCachedSession = Boolean(getStorage().getItem("token"));
    const unsubscribe = firebase.auth().onAuthStateChanged((identity) => {
      const first = initialNotification;
      initialNotification = false;
      if (!identity) {
        if (!first) clearPendingEmailSignIn();
        if (inlineSignInRef.current) inlineSignInRef.current.cancelled = true;
        ++generationRef.current;
        identityRef.current = null;
        requestRef.current = null;
        resolvedRef.current = null;
        inlineFailureRef.current = null;
        clearCachedAuthentication();
        setAuthState({});
        setFirebaseUser(null);
        setSession(null);
        setSessionStatus("signed-out");
        return;
      }
      if (logoutRef.current) return;
      identityRef.current = identity;
      setFirebaseUser(identity);
      if (inlineSignInRef.current) {
        const owner = inlineSignInRef.current;
        if ((owner.kind === 'emailChange' && owner.cancelled) ||
          (owner.expectedUid && owner.expectedUid !== identity.uid) ||
          (owner.kind !== 'emailChange' && (!identity.email || identity.email.trim().toLowerCase() !== owner.email))) {
          owner.cancelled = true;
          owner.replacement = identity;
          ++generationRef.current;
          setSessionStatus('loading');
        } else if (owner.kind !== 'emailChange') setSessionStatus('callback');
        return;
      }
      if (inlineFailureRef.current === identity.uid) {
        setSessionStatus('error');
        return;
      }
      // Both account-change callbacks and ordinary sign-in callbacks have owners.
      // They must finish Firebase work before any Core bootstrap or redirect.
      if (hasFirebaseAction()) {
        setSessionStatus("callback");
        return;
      }
      if (first && !hadCachedSession && !localStorage.getItem("rememberMe")) {
        clearCachedAuthentication();
        setAuthState({});
        setSessionStatus("signed-out");
        firebase.auth().signOut().catch(() => {});
        return;
      }
      return refreshSession({ firebaseUser: identity }).catch(() => {});
    });
    return () => {
      mountedRef.current = false;
      if (inlineSignInRef.current) inlineSignInRef.current.cancelled = true;
      // Invalidate the current generation, including work started after mount.
      ++generationRef.current;
      requestRef.current = null;
      unsubscribe();
    };
  }, [refreshSession]);

  const setAuthInfo = useCallback(({ token, user }) => {
    const storage = getStorage();
    const impersonating = Boolean(storage.getItem("impersonate"));
    const cachedUser = readCachedUser(impersonating ? "impersonate" : "user");
    const access = readCachedSessionAccess(user) ||
      (user?._id === cachedUser._id ? readCachedSessionAccess(cachedUser) : null);
    storage.setItem("token", token);
    // Explicit updates may carry a new club before the caller updates storage.
    const preferredClubId = user?.club?._id || storage.getItem("activeClubId");
    const result = describeConsoleSession({ user, club: user?.club, clubs: user?.clubs },
      preferredClubId, impersonating, access);
    saveSession(result, token);
    // AppShell/account-link updates supersede a previously resolved snapshot.
    resolvedRef.current = null;
  }, [saveSession]);

  const logout = useCallback(async () => {
    logoutRef.current = true;
    if (inlineSignInRef.current) inlineSignInRef.current.cancelled = true;
    inlineFailureRef.current = null;
    ++generationRef.current;
    requestRef.current = null;
    resolvedRef.current = null;
    identityRef.current = null;
    clearCachedAuthentication();
    clearPendingEmailChange();
    clearPendingMobileChange();
    clearPendingEmailSignIn();
    setAuthState({});
    setFirebaseUser(null);
    setSession(null);
    setSessionStatus("signed-out");
    navigate(LOGIN_ROUTE);
    try { await firebase.auth().signOut(); }
    catch (_) { localStorage.removeItem("rememberMe"); }
    finally { logoutRef.current = false; }
  }, [navigate]);

  const impersonating = Boolean(getStorage().getItem("impersonate"));
  const effectiveUser = impersonating ? authState.impersonate : authState.user;
  const isAuthenticated = () => Boolean(authState.token && sessionStatus === "ready" && session?.kind === "established" &&
    effectiveUser?._id && hasManagerRole(effectiveUser));
  const isSuperAdmin = () => Boolean(authState.user?.roles?.includes(USER_ROLES.SUPER_ADMIN));

  return (
    <AuthContext.Provider value={{
      authState: { ...authState, user: effectiveUser },
      firebaseUser, sessionStatus, session, refreshSession, completeEmailCodeSignIn, completeEmailCodeChange, inlineSignInPending, isInlineSignInPending,
      setAuthState: setAuthInfo, logout, isAuthenticated, isAdmin: isSuperAdmin,
      setImpersonate: (user) => {
        resolvedRef.current = null;
        setAuthState((current) => ({ ...current, impersonate: user }));
      },
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
export { AuthContext, AuthProvider };
